import { useEffect, useState } from "react";
import type { StakeholderSetting } from "../../../hooks";
import {
  isValidTemplateSheetUrl,
  RESPONDENT_TYPE_LABELS,
} from "../stakeholderSetting";

interface StakeholderSettingDialogProps {
  open: boolean;
  /** 編集対象。null なら新規追加 */
  setting: StakeholderSetting | null;
  onClose: () => void;
  onSubmit: (form: StakeholderSettingForm) => Promise<boolean>;
}

export interface StakeholderSettingForm {
  name: string;
  respondentType: StakeholderSetting["respondentType"];
  scriptTemplateSheetUrl: string;
  contentsTemplateSheetUrl: string;
}

const EMPTY_FORM: StakeholderSettingForm = {
  name: "",
  respondentType: "SUPERVISOR",
  scriptTemplateSheetUrl: "",
  contentsTemplateSheetUrl: "",
};

const SHEET_URL_HINT =
  "指定フォルダ配下のシートを、ブラウザで対象のシートを開いた状態のURL（?gid= を含む形式）で指定してください";

export function StakeholderSettingDialog({
  open,
  setting,
  onClose,
  onSubmit,
}: StakeholderSettingDialogProps) {
  const [form, setForm] = useState<StakeholderSettingForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // ダイアログは常時マウントされるため、開くたびに編集対象から再シードする
  useEffect(() => {
    if (!open) return;
    setForm(
      setting
        ? {
            name: setting.name,
            respondentType: setting.respondentType,
            scriptTemplateSheetUrl: setting.scriptTemplateSheetUrl,
            contentsTemplateSheetUrl: setting.contentsTemplateSheetUrl,
          }
        : EMPTY_FORM,
    );
  }, [open, setting]);

  if (!open) return null;

  const scriptUrlInvalid =
    form.scriptTemplateSheetUrl.length > 0 &&
    !isValidTemplateSheetUrl(form.scriptTemplateSheetUrl);
  const contentsUrlInvalid =
    form.contentsTemplateSheetUrl.length > 0 &&
    !isValidTemplateSheetUrl(form.contentsTemplateSheetUrl);
  const canSubmit =
    form.name.trim().length > 0 &&
    isValidTemplateSheetUrl(form.scriptTemplateSheetUrl) &&
    isValidTemplateSheetUrl(form.contentsTemplateSheetUrl) &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const succeeded = await onSubmit({ ...form, name: form.name.trim() });
    setSubmitting(false);
    if (succeeded) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[560px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 font-semibold text-[var(--color-text-primary)] text-lg">
          {setting ? "インタビュー設定を編集" : "インタビュー設定を追加"}
        </h3>
        <p className="mt-0 mb-5 text-[13px] text-[var(--color-text-secondary)]">
          回答者の立場ごとに1つ作成します
        </p>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="stakeholderSettingName"
              className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
            >
              名前
            </label>
            <input
              id="stakeholderSettingName"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="例: 上司向け（シニア・ジュニア）"
              required
              className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-sm outline-none"
            />
          </div>
          <div className="mb-4">
            <span className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm">
              回答者種別
            </span>
            <div className="flex flex-wrap gap-4">
              {Object.entries(RESPONDENT_TYPE_LABELS).map(
                ([respondentType, label]) => (
                  <label
                    key={respondentType}
                    className="flex cursor-pointer items-center gap-1.5"
                  >
                    <input
                      type="radio"
                      name="respondentType"
                      value={respondentType}
                      checked={form.respondentType === respondentType}
                      onChange={() =>
                        setForm({
                          ...form,
                          respondentType:
                            respondentType as StakeholderSettingForm["respondentType"],
                        })
                      }
                      className="accent-[var(--color-primary-500)]"
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ),
              )}
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor="scriptTemplateSheetUrl"
              className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
            >
              設問シートのテンプレートURL
            </label>
            <input
              id="scriptTemplateSheetUrl"
              type="url"
              value={form.scriptTemplateSheetUrl}
              onChange={(e) =>
                setForm({ ...form, scriptTemplateSheetUrl: e.target.value })
              }
              placeholder="https://docs.google.com/spreadsheets/d/.../edit?gid=0"
              required
              className={`box-border w-full rounded-md border px-3 py-2.5 text-sm outline-none ${
                scriptUrlInvalid
                  ? "border-[#ef4444]"
                  : "border-[var(--color-border-default)]"
              }`}
            />
            {scriptUrlInvalid && (
              <p className="mt-1 mb-0 text-[#ef4444] text-[13px]">
                {SHEET_URL_HINT}
              </p>
            )}
          </div>
          <div className="mb-5">
            <label
              htmlFor="contentsTemplateSheetUrl"
              className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
            >
              要約シートのテンプレートURL
            </label>
            <input
              id="contentsTemplateSheetUrl"
              type="url"
              value={form.contentsTemplateSheetUrl}
              onChange={(e) =>
                setForm({ ...form, contentsTemplateSheetUrl: e.target.value })
              }
              placeholder="https://docs.google.com/spreadsheets/d/.../edit?gid=0"
              required
              className={`box-border w-full rounded-md border px-3 py-2.5 text-sm outline-none ${
                contentsUrlInvalid
                  ? "border-[#ef4444]"
                  : "border-[var(--color-border-default)]"
              }`}
            />
            <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
              {SHEET_URL_HINT}
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={`rounded-md border border-[var(--color-text-secondary)] bg-white px-4 py-2.5 font-medium text-[var(--color-text-secondary)] text-sm ${submitting ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`rounded-md border-none px-4 py-2.5 font-medium text-sm text-white ${
                canSubmit
                  ? "cursor-pointer bg-[var(--color-primary-500)]"
                  : "cursor-not-allowed bg-[#9ca3af]"
              }`}
            >
              {submitting ? "保存中..." : setting ? "更新" : "追加"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
